
import { supabase } from "@/integrations/supabase/client";

export interface ConversationStatusResponse {
  success: boolean;
  conversation?: {
    id: string;
    status: string;
    duration_seconds?: number;
    creation_time?: string;
    completion_time?: string;
    call_sid?: string;
    transcript?: string;
    meta?: Record<string, any>;
  };
  error?: string;
}

/**
 * Fetches the status and details of an ElevenLabs conversation
 */
export const getConversationStatus = async (
  conversationId: string
): Promise<ConversationStatusResponse> => {
  try {
    console.log(`Checking status for conversation: ${conversationId}`);
    
    const { data, error } = await supabase.functions.invoke("get-elevenlabs-conversation", {
      body: { conversationId },
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error || "Failed to fetch conversation details");

    return { 
      success: true, 
      conversation: data.conversation
    };
  } catch (error) {
    console.error("Error fetching conversation status:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};

/**
 * Updates the call record in the database with status information from the conversation
 */
export const updateCallFromConversationStatus = async (
  callId: string,
  conversationId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // First, get the conversation status
    const statusResponse = await getConversationStatus(conversationId);
    
    if (!statusResponse.success || !statusResponse.conversation) {
      throw new Error(statusResponse.error || "Failed to get conversation status");
    }
    
    const conversation = statusResponse.conversation;
    
    // Map ElevenLabs conversation status to our call status
    let callStatus: string;
    switch (conversation.status?.toLowerCase()) {
      case 'completed':
        callStatus = 'completed';
        break;
      case 'failed':
      case 'error':
        callStatus = 'failed';
        break;
      case 'in_progress':
      case 'in-progress':
        callStatus = 'in-progress';
        break;
      case 'no_answer':
      case 'no-answer':
        callStatus = 'failed';
        break;
      default:
        callStatus = conversation.status || 'unknown';
    }
    
    // Prepare call metadata
    const metaData = {
      duration: conversation.duration_seconds,
      answer_time: conversation.creation_time,
      end_time: conversation.completion_time,
      hangup_cause: conversation.status === 'failed' ? 'Call failed or not answered' : undefined,
      ...conversation.meta
    };
    
    // Update the call in the database
    const { error } = await supabase
      .from('calls')
      .update({
        status: callStatus,
        transcript: conversation.transcript || null,
        meta: metaData
      })
      .eq('id', callId);
    
    if (error) throw error;
    
    // If call is completed or failed, also update the lead status
    if (callStatus === 'completed' || callStatus === 'failed') {
      // First get the lead_id from the call
      const { data: callData, error: callError } = await supabase
        .from('calls')
        .select('lead_id')
        .eq('id', callId)
        .single();
      
      if (callError) throw callError;
      
      // Then update the lead status
      const leadStatus = callStatus === 'completed' ? 'contacted' : 'failed';
      const { error: leadError } = await supabase
        .from('leads')
        .update({ status: leadStatus })
        .eq('id', callData.lead_id);
      
      if (leadError) throw leadError;
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error updating call from conversation status:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};

/**
 * Poll for conversation status updates until the call is completed or failed
 */
export const pollConversationStatus = async (
  callId: string,
  conversationId: string,
  intervalMs: number = 5000,
  maxAttempts: number = 24 // Default 2 minutes max polling time
): Promise<{ success: boolean; status?: string; error?: string }> => {
  try {
    let attempts = 0;
    
    const checkStatus = async (): Promise<{ success: boolean; status?: string; error?: string }> => {
      attempts++;
      
      // Get current conversation status
      const { success, conversation, error } = await getConversationStatus(conversationId);
      
      if (!success) {
        console.error(`Error checking conversation status (attempt ${attempts}):`, error);
        return { success: false, error };
      }
      
      console.log(`Conversation status (attempt ${attempts}):`, conversation?.status);
      
      // Update call record with current status
      await updateCallFromConversationStatus(callId, conversationId);
      
      // Check if call has reached a terminal state
      const status = conversation?.status?.toLowerCase();
      if (
        status === 'completed' || 
        status === 'failed' || 
        status === 'error' || 
        status === 'no_answer' ||
        status === 'no-answer'
      ) {
        return { success: true, status };
      }
      
      // Return if we've reached max attempts
      if (attempts >= maxAttempts) {
        return { 
          success: false, 
          error: `Max polling attempts (${maxAttempts}) reached` 
        };
      }
      
      // Wait for interval and try again
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await checkStatus();
          resolve(result);
        }, intervalMs);
      });
    };
    
    return await checkStatus();
  } catch (error) {
    console.error("Error polling conversation status:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};
